import * as React from "react";
import {SyntheticEvent} from "react";
import {observer} from "mobx-react";
import {Link} from "react-router-dom";
import {action, runInAction} from "mobx";

import "./topic-switcher.scss";

import {Icon as SemanticIcon, Menu, Sidebar} from "semantic-ui-react";

import {TopicConfiguration} from "../../../config/topic-configuration";
import {Icon} from "../icon/icon";
import {PathUtils} from "../../../utils/path-utils";
import {IAppStore, withStore} from "../../stores/abstract-app-store";


export interface TopicSwitcherProps {
    topics: TopicConfiguration[];
    small?: boolean;
    store?: IAppStore; // this is injected by withStore
}

@observer
class TopicSwitcherBase extends React.Component<TopicSwitcherProps, any> {
    widgetKey = "topic-switcher";
    constructor(props: TopicSwitcherProps) {
        super(props);
        if (typeof this.props.small === "boolean" && this.props.small) {
            this.widgetKey += "-small";
        }

        this.handleMenuOpen = this.handleMenuOpen.bind(this);
        this.handleToggle = this.handleToggle.bind(this);
    }

    @action
    setTopic(topic: TopicConfiguration) {
        runInAction(() => {
            const {topics, store} = this.props;
            if (!store) {
                return;
            }
            // if a top-level topic was clicked, select the first child
            if (!topic.routes) {
                topic = store.mapViewerConfig.getFirstSubtopic(topic, topics);
            }
            // Falls sich das Subtopic ändert, Popup und Tools (z.B. Höhenprofil) schliessen
            if(topic.id !== store.appStore.subtopicId) {
                store.PopupStore.setVisible(false);
                store.uiStore.setActiveTool("");
            }
            store.appStore.setSubtopic(topic);
            store.appStore.setRoutePath(PathUtils.joinPath(store.appStore.topic.routes[0], topic?.routes[0]));
        });
    }

    handleMenuOpen(event: SyntheticEvent, data: any) {
        event.preventDefault();
        const {topics, store} = this.props;
        this.handleToggle();
        if(data.name === "openMapFinder"){
            store?.MapFinderStore.setVisible(true);
            return;
        }
        const topic = topics?.find((t) => (t.id === data.name));
        if (!topic) {
            return;
        }
        this.setTopic(topic);
    }

    handleToggle(event?: SyntheticEvent) {
        const {store} = this.props;
        if (store?.TopicSwitcherStore.visible) {
            store?.TopicSwitcherStore.setVisible(false);
        } else {
            store?.TopicSwitcherStore.setVisible(true);
        }
        if(event) {
            event.preventDefault();
        }
    }

    componentDidMount(): void {
        const {store} = this.props;
        if (store?.uiStore.widgets.has(this.widgetKey)) {
            return;
        }
        store?.uiStore.addWidget(this.widgetKey, this);
    }

    componentWillUnmount(): void {
        const {store} = this.props;
        store?.uiStore.removeWidget(this.widgetKey);
    }

    render(): React.ReactElement | null {
        const {small, topics, store} = this.props;
        if (!store) {
            return null;
        }
        const {subtopic, topic} = store.appStore;
        const visible = store.TopicSwitcherStore.visible;

        if (!subtopic || !topics) {
            return null;
        }

        const menuItems = this.composeMenu(topics, topic, subtopic, small);

        menuItems.unshift(
            <div className={"item geo-topic-switcher-header"} key={"topic-switcher-header"}>
                {!small? <Icon name={"signpost"} className={"icon--topic-switcher"} />: null}
                {store.mapViewerConfig.topicSwitcher?.description?
                    store.mapViewerConfig.topicSwitcher?.description : "Thema in dieser Karte wählen"}
                {!small?
                    <div onClick={this.handleToggle} className={"geo-close"}><Icon name="navigate_cross" /></div>: null}
            </div>
        );
        if (small) {
            return (
                <Menu
                    className="geo-topic-switcher-menu menu-small"
                    vertical
                    visible={visible.toString()}>
                    {menuItems}
                </Menu>
            );
        } else {
            menuItems.push(
                <Menu.Item
                    as={Link}
                    onClick={this.handleMenuOpen}
                    key={"MapFinder"}
                    name={"openMapFinder"}
                    position={small ? "left" : "right"}
                    className={"geo-map-finder"}>
                    {small? <SemanticIcon name={"th"} /> : null}
                Weitere Karten
                    {!small ? <SemanticIcon name={"th"} /> : null}
                </Menu.Item>
            );
            return <Sidebar
                className="geo-topic-switcher-menu"
                as={Menu}
                animation="overlay"
                direction={"top"}
                vertical
                visible={visible}>
                {menuItems}
            </Sidebar>;
        }
    }

    private composeMenu(topics: TopicConfiguration[],
                        topic: TopicConfiguration,
                        subtopic: TopicConfiguration,
                        small: boolean | undefined) {
        const someTopicHasChildren = topics.some((t) => t.parentId);

        const parentTopics = topics.filter((t) => !t.parentId);
        const allChildrenHidden = topics.filter((t) => t.parentId).every((tc) => tc.hidden);
        return parentTopics.flatMap((parentConfig) => {
            const items: React.ReactElement[] = [];
            items.push(
                <Menu.Item
                    as={Link}
                    to={PathUtils.joinPath(topic.routes[0], (parentConfig.routes ? parentConfig.routes[0] : parentConfig.id))}
                    onClick={this.handleMenuOpen}
                    key={parentConfig.id}
                    name={parentConfig.id}
                    active={parentConfig.id === subtopic.id}
                    position={small ? "left" : "right"}
                    className={someTopicHasChildren && !allChildrenHidden ? "level-1" : "level-2"}>
                    {small && parentConfig.icon ? <Icon name={parentConfig.icon}/> : null}
                    {parentConfig.name ? parentConfig.name : parentConfig.label}
                    {!small && parentConfig.icon ? <Icon name={parentConfig.icon}/> : null}
                </Menu.Item>
            );

            const childTopics =  topics.filter((tc) => parentConfig.id === tc.parentId);
            childTopics.forEach((childConfig) => {
                if (childConfig.hidden) {
                    return;
                }

                items.push(
                    <Menu.Item
                        as={Link}
                        to={PathUtils.joinPath(topic.routes[0], (childConfig.routes ? childConfig.routes[0] : childConfig.id))}
                        onClick={this.handleMenuOpen}
                        key={childConfig.id}
                        name={childConfig.id}
                        active={childConfig.id === subtopic.id}
                        position={small ? "left" : "right"}
                        className={"level-2"}>
                        {small && childConfig.icon ? <Icon name={childConfig.icon}/> : null}
                        {childConfig.name ? childConfig.name : childConfig.label}
                        {!small && childConfig.icon ? <Icon name={childConfig.icon}/> : null}
                    </Menu.Item>
                );
            });
            return items;
        });
    }
}

export const TopicSwitcher = withStore(TopicSwitcherBase);
